<template>
  <BottomSheetModal 
    v-model="isOpen" 
    :confirmDisabled="!isFormValid || (isEditing && !isDirty)"
    :hideHeader="currentView !== 'main'"
    @confirm="handleSave"
    @close="handleClose"
  >
    <template #title>{{ isEditing ? $t('leagueForm.editTitle') : $t('leagueForm.createTitle') }}</template>

    <div class="views-container">
      <!-- Main View -->
      <div class="main-view" :class="{ 'main-view--hidden': currentView !== 'main' }">
        <!-- League Name Section -->
        <IosSection form :title="$t('leagueForm.nameSection')" required>
          <IosInput 
            v-model="form.name" 
            :placeholder="$t('leagueForm.namePlaceholder')"
            :maxlength="24"
            first
            last
          />
        </IosSection>

        <!-- Settings Section -->
        <IosSection form :title="$t('leagueForm.settingsSection')">
          <IosRow 
            :label="$t('leagueForm.location')" 
            :value="form.location?.label || ''"
            clickable
            required
            first
            @click="currentView = 'location'"
          />
          
          <IosRow 
            :label="$t('leagueForm.logo')" 
            clickable
            @click="currentView = 'logo'"
          >
            <template #value>
              <div class="logo-preview">
                <img :src="logoPreview || defaultLogoUrl" alt="Logo" />
              </div>
            </template>
          </IosRow>

          <IosRow 
            :label="$t('leagueForm.type')" 
            :value="form.type === 'academy' ? $t('leagueForm.typeAcademy') : $t('leagueForm.typeCompetition')"
            clickable
            @click="currentView = 'typeSelect'"
          />

          <!-- Display/Repre tým - jen pro akademie -->
          <IosRow 
            v-if="form.type === 'academy'"
            :label="$t('leagueForm.repreTeam')"
            :value="form.displayTeam?.name || $t('leagueForm.notSelected')"
            clickable
            @click="openDisplayTeamSelect"
          />

          <IosRow 
            :label="$t('leagueForm.eventColor')" 
            clickable
            @click="currentView = 'colorPicker'"
          >
            <template #value>
              <div class="color-preview" :style="{ background: getCalendarColorHex(form.color) }"></div>
            </template>
          </IosRow>
          
          <IosSwitch 
            v-model="form.isPublic" 
            :label="$t('leagueForm.public')"
          />

          <IosRow 
            :label="t('bankAccount')"
            :value="bankAccountDisplay"
            clickable
            @click="currentView = 'bankAccount'"
          />

          <IosSwitch 
            v-model="form.preferLeagueAccount" 
            :label="$t('leagueForm.leaguePayments')"
            :subtitle="$t('leagueForm.leaguePaymentsDesc')"
            last
          />
        </IosSection>

        <!-- Colors Section -->
        <IosSection form :title="t('teamColors')">
          <IosRow 
            :label="t('primaryColor')" 
            clickable
            @click="openTeamColorPicker('primary')"
          >
            <template #value>
              <div class="color-preview" :style="{ background: form.primaryColor }"></div>
            </template>
          </IosRow>
          
          <IosRow 
            :label="t('secondaryColor')" 
            clickable
            last
            @click="openTeamColorPicker('secondary')"
          >
            <template #value>
              <div class="color-preview" :style="{ background: form.secondaryColor }"></div>
            </template>
          </IosRow>
        </IosSection>

        <!-- Admins Section -->
        <RoleSection
          :users="form.admins"
          :title="t('admins')"
          :role-label="t('admin')"
          :add-label="t('addAdmin')"
          :empty-label="t('noAdmins')"
          editable
          required
          :min-count="1"
          @add="openInviteModal('admin')"
          @remove="removeAdmin"
          @click-user="openProfile"
        />

        <!-- Timekeepers Section (only for leagues, not academies) -->
        <RoleSection
          v-if="form.type !== 'academy'"
          :users="form.timekeepers"
          :title="t('timekeepers')"
          :role-label="t('timekeeper')"
          :add-label="t('addTimekeeper')"
          :empty-label="t('noTimekeepers')"
          editable
          @add="openInviteModal('timekeeper')"
          @remove="removeTimekeeper"
          @click-user="openProfile"
        />

        <!-- Seasons Section -->
        <IosSection form :title="$t('leagueForm.seasonsSection')">
          <template v-if="isEditing && seasonsLoading">
            <IosRow :label="$t('leagueForm.loadingSeasons')" first last />
          </template>
          <template v-else-if="allSeasons.length > 0">
            <IosRow
              v-for="(season, index) in allSeasons"
              :key="season._id || season._localId"
              :label="season.year || formatSeasonDates(season)"
              :subtitle="season._localId ? $t('leagueForm.newUnsaved') : (season.active ? $t('leagueForm.seasonActive') : $t('leagueForm.seasonInactive'))"
              :first="index === 0"
              clickable
              @click="editSeason(season)"
            >
              <template #value>
                <div class="season-actions">
                  <button class="season-delete-btn" @click.stop="deleteSeason(season._id || season._localId)">
                    <v-icon size="18" color="error">mdi-delete</v-icon>
                  </button>
                </div>
              </template>
            </IosRow>
          </template>
          <div v-else class="empty-section">
            {{ $t('leagueForm.noSeasons') }}
          </div>
          <IosAddRow
            :label="$t('leagueForm.newSeason')"
            last
            @click="openSeasonForm()"
          />
        </IosSection>

        <!-- Delete Organization (only in edit mode) -->
        <div v-if="isEditing" class="delete-org-section">
          <button class="delete-org-btn" @click="showDeleteOrgConfirm = true; deleteOrgConfirmInput = ''">
            {{ $t('deleteOrganization') }}
          </button>
        </div>

      </div>

      <!-- Location Search View -->
      <Transition name="slide">
        <div v-if="currentView === 'location'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('leagueForm.selectLocation') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <div class="search-input-wrapper-ios">
                <v-icon size="20" class="search-icon">mdi-magnify</v-icon>
                <input 
                  v-model="locationSearch"
                  type="text"
                  class="search-input"
                  :placeholder="$t('leagueForm.searchCity')"
                  @input="onLocationSearch"
                />
                <v-progress-circular v-if="searchingCities" indeterminate size="18" width="2" class="ml-2" />
              </div>
            </IosSection>
        
            <IosSection form v-if="cityResults.length > 0">
              <IosRow 
                v-for="(city, index) in cityResults" 
                :key="city.id"
                :label="city.label"
                :subtitle="city.subtitle"
                :first="index === 0"
                :last="index === cityResults.length - 1"
                clickable
                @click="selectLocation(city)"
              />
            </IosSection>
            
            <div v-else-if="!cityResults.length && locationSearch.length >= 2 && !searchingCities" class="search-empty">
              {{ $t('leagueForm.noResults') }}
            </div>
          </div>
        </div>
      </Transition>

      <!-- Logo Selection View -->
      <Transition name="slide">
        <div v-if="currentView === 'logo'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('leagueForm.logo') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <div class="logo-section">
              <div class="current-logo">
                <img :src="logoPreview || defaultLogoUrl" alt="Logo" />
              </div>
          
              <div class="logo-actions">
                <v-btn 
                  variant="tonal" 
                  color="primary"
                  @click="selectLogo"
                >
                  <v-icon start>mdi-image</v-icon>
                  {{ $t('leagueForm.selectImage') }}
                </v-btn>
            
                <v-btn 
                  v-if="logoPreview"
                  variant="text" 
                  color="error"
                  @click="removeLogo"
                >
                  {{ $t('leagueForm.remove') }}
                </v-btn>
              </div>
          
              <input 
                ref="logoInput"
                type="file"
                accept="image/*"
                class="hidden-input"
                @change="onLogoSelected"
              />
            </div>
          </div>
        </div>
      </Transition>

      <!-- Type Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'typeSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('leagueForm.typeOrgSection') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                :label="$t('leagueForm.competitionLabel')"
                :subtitle="$t('leagueForm.competitionDesc')"
                clickable
                first
                @click="form.type = 'league'; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.type === 'league'" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
              <IosRow 
                :label="$t('leagueForm.academyLabel')"
                :subtitle="$t('leagueForm.academyDesc')"
                clickable
                last
                @click="form.type = 'academy'; currentView = 'main'"
              >
                <template #value>
                  <v-icon v-if="form.type === 'academy'" size="20" color="success">mdi-check</v-icon>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Color Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'colorPicker'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('leagueForm.eventColor') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <IosRow 
                v-for="(c, index) in calendarColors" 
                :key="c.value"
                :label="c.label"
                clickable
                :first="index === 0"
                :last="index === calendarColors.length - 1"
                @click="form.color = c.value; currentView = 'main'"
              >
                <template #value>
                  <div class="d-flex align-center" style="gap: 10px">
                    <div class="color-preview" :style="{ background: c.hex }"></div>
                    <v-icon v-if="form.color === c.value" size="20" color="success">mdi-check</v-icon>
                  </div>
                </template>
              </IosRow>
            </IosSection>
          </div>
        </div>
      </Transition>

      <!-- Team Color Picker View -->
      <Transition name="slide">
        <div v-if="currentView === 'teamColorPicker'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">
              {{ editingTeamColor === 'primary' ? t('primaryColor') : t('secondaryColor') }}
            </div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <div class="color-picker-section">
              <v-color-picker 
                v-model="currentTeamColorValue"
                :modes="['hex', 'rgb']"
                show-swatches
                swatches-max-height="200px"
                width="100%"
                theme="dark"
                class="color-picker"
              />
            </div>
          </div>
        </div>
      </Transition>

      <!-- Bank Account View -->
      <Transition name="slide">
        <div v-if="currentView === 'bankAccount'" class="subview">
          <BankAccountPicker
            v-model="bankAccountValue"
            @back="currentView = 'main'"
          />
        </div>
      </Transition>

      <!-- Display Team Select View -->
      <Transition name="slide">
        <div v-if="currentView === 'displayTeamSelect'" class="subview">
          <div class="subview-header">
            <button class="back-btn" @click="currentView = 'main'">
              <v-icon size="22">mdi-chevron-left</v-icon>
            </button>
            <div class="subview-title">{{ $t('leagueForm.repreTeam') }}</div>
            <div class="spacer"></div>
          </div>
          
          <div class="subview-scroll">
            <IosSection form>
              <template v-if="loadingMyTeams">
                <IosRow :label="$t('leagueForm.loadingTeams')" first last />
              </template>
              <template v-else-if="myTeams.length > 0">
                <!-- Možnost zrušit výběr -->
                <IosRow 
                  :label="$t('leagueForm.noTeam')"
                  :subtitle="$t('leagueForm.noTeamDesc')"
                  clickable
                  first
                  @click="selectDisplayTeam(null)"
                >
                  <template #value>
                    <v-icon v-if="!form.displayTeam" size="20" color="success">mdi-check</v-icon>
                  </template>
                </IosRow>
                <IosRow 
                  v-for="(team, index) in myTeams"
                  :key="team._id"
                  :label="team.name"
                  clickable
                  :last="index === myTeams.length - 1"
                  @click="selectDisplayTeam(team)"
                >
                  <template #value>
                    <div class="d-flex align-center" style="gap: 10px">
                      <img 
                        :src="getTeamImage(team.imageUrl)" 
                        class="team-pick-logo" 
                        alt=""
                      />
                      <v-icon v-if="form.displayTeam?._id === team._id" size="20" color="success">mdi-check</v-icon>
                    </div>
                  </template>
                </IosRow>
              </template>
              <template v-else>
                <IosRow :label="$t('leagueForm.noAvailableTeams')" first last />
              </template>
            </IosSection>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Season Form Modal -->
    <SeasonFormModal
      v-model="showSeasonForm"
      :league-id="leagueObjectId || props.leagueId || ''"
      :season="seasonToEdit"
      :local-mode="!isEditing"
      @saved="handleSeasonSaved"
    />

    <!-- Delete Season Confirm -->
    <CenteredModal v-model="showDeleteSeasonConfirm" :show-close-button="true" max-width="350px">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-icon">
          <v-icon size="48" color="error">mdi-delete-alert</v-icon>
        </div>
        <h3 class="delete-confirm-title">{{ $t('leagueForm.deleteSeasonTitle') }}</h3>
        <p class="delete-confirm-subtitle">
          {{ $t('leagueForm.deleteSeasonConfirm') }}
        </p>
        <div class="delete-confirm-actions">
          <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelDeleteSeason">
            {{ $t('leagueForm.cancel') }}
          </button>
          <button 
            class="delete-confirm-btn delete-confirm-btn--confirm" 
            :disabled="deleteCountdown > 0"
            @click="confirmDeleteSeason"
          >
            {{ $t('leagueForm.delete') }}{{ deleteCountdown > 0 ? ` (${deleteCountdown})` : '' }}
          </button>
        </div>
      </div>
    </CenteredModal>

    <!-- Invite Persons Modal -->
    <InvitePersonsModal
      v-model="showInviteModal"
      :title="inviteModalTitle"
      :confirm-button-label="$t('leagueForm.add')"
      :already-selected="allSelectedUsers"
      :entity-id="props.leagueId || ''"
      entity-type="league"
      :role="inviteRole"
      @done="handleInviteDone"
    />

    <PlayerCardModal ref="playerCardRef" />

    <!-- Delete Organization Confirm -->
    <CenteredModal v-model="showDeleteOrgConfirm" :show-close-button="true" max-width="350px">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-icon">
          <v-icon size="48" color="error">mdi-delete-alert</v-icon>
        </div>
        <h3 class="delete-confirm-title">{{ $t('deleteOrganization') }}</h3>
        <p class="delete-confirm-subtitle">
          {{ $t('deleteOrganizationConfirm', { name: form.name }) }}
        </p>
        <div class="delete-confirm-input-wrap">
          <input
            v-model="deleteOrgConfirmInput"
            class="delete-confirm-input"
            :placeholder="$t('typeToConfirmDelete', { word: $t('delete').toLowerCase() })"
            autocomplete="off"
          />
        </div>
        <div class="delete-confirm-actions">
          <button class="delete-confirm-btn delete-confirm-btn--cancel" @click="cancelDeleteOrg">
            {{ $t('cancel') }}
          </button>
          <button 
            class="delete-confirm-btn delete-confirm-btn--confirm" 
            :disabled="!isDeleteOrgConfirmValid"
            @click="executeDeleteOrg"
          >
            {{ $t('delete') }}
          </button>
        </div>
      </div>
    </CenteredModal>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/user/userStore';
import BottomSheetModal from '@/components/BottomSheetModal.vue';
import InvitePersonsModal from '@/components/InvitePersonsModal.vue';
import BankAccountPicker from '@/components/BankAccountPicker.vue';
import SeasonFormModal from '@/components/league/SeasonFormModal.vue';
import CenteredModal from '@/components/CenteredModal.vue';
import { IosSection, IosRow, IosInput, IosSwitch, IosAddRow } from '@/components/ios';
import RoleSection from '@/components/Team/RoleSection.vue';
import PlayerCardModal from '@/components/PlayerCardModal.vue';
import type { ISeason } from '@/types';
import { mapUserLite } from '@/components/Team/teamRoles';
import type { UserLite } from '@/components/Team/teamRoles';
import axiosInstance from '@/services/axiosInstance';
import { getLeagueImage, getTeamImage } from '@/utils/imageUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const { t } = useI18n({ useScope: 'global' });
const userStore = useUserStore();

type ViewType = 'main' | 'location' | 'logo' | 'colorPicker' | 'typeSelect' | 'bankAccount' | 'teamColorPicker' | 'displayTeamSelect';

interface LocationOption {
  id: string | number;
  label: string;
  subtitle?: string;
  lat?: string;
  lon?: string;
  raw?: any;
}

interface LeagueForm {
  name: string;
  location: LocationOption | null;
  logoFile: File | null;
  logoUrl: string;
  isPublic: boolean;
  type: 'league' | 'academy';
  color: string;
  primaryColor: string;
  secondaryColor: string;
  admins: UserLite[];
  timekeepers: UserLite[];
  bankAccountPrefix: string;
  bankAccountNumber: string;
  bankCode: string;
  preferLeagueAccount: boolean;
  displayTeam: { _id: string; name: string; imageUrl?: string } | null;
}

const calendarColors = computed(() => [
  { value: 'red', hex: '#dc2626', label: t('colors.red') },
  { value: 'blue', hex: '#2563eb', label: t('colors.blue') },
  { value: 'green', hex: '#16a34a', label: t('colors.green') },
  { value: 'orange', hex: '#ea580c', label: t('colors.orange') },
  { value: 'yellow', hex: '#ca8a04', label: t('colors.yellow') },
  { value: 'purple', hex: '#9333ea', label: t('colors.purple') },
  { value: 'pink', hex: '#db2777', label: t('colors.pink') },
  { value: 'teal', hex: '#0d9488', label: t('colors.teal') },
  { value: 'indigo', hex: '#4f46e5', label: t('colors.indigo') },
  { value: 'gray', hex: '#475569', label: t('colors.gray') },
]);

const getCalendarColorHex = (value: string) => {
  return calendarColors.value.find(c => c.value === value)?.hex || '#dc2626';
};

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  leagueId: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'saved', 'deleted']);

// State
const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const isEditing = computed(() => !!props.leagueId);
const currentView = ref<ViewType>('main');

const form = ref<LeagueForm>({
  name: '',
  location: null,
  logoFile: null,
  logoUrl: '',
  isPublic: true,
  type: 'league',
  color: 'red',
  primaryColor: '#1a237e',
  secondaryColor: '#ffc107',
  admins: [],
  timekeepers: [],
  bankAccountPrefix: '',
  bankAccountNumber: '',
  bankCode: '',
  preferLeagueAccount: true,
  displayTeam: null,
});

// Team Colors
const editingTeamColor = ref<'primary' | 'secondary'>('primary');
const currentTeamColorValue = computed({
  get: () => editingTeamColor.value === 'primary' ? form.value.primaryColor : form.value.secondaryColor,
  set: (val) => {
    if (editingTeamColor.value === 'primary') {
      form.value.primaryColor = val;
    } else {
      form.value.secondaryColor = val;
    }
  }
});

const openTeamColorPicker = (type: 'primary' | 'secondary') => {
  editingTeamColor.value = type;
  currentView.value = 'teamColorPicker';
};

// Location search
const locationSearch = ref('');
const cityResults = ref<LocationOption[]>([]);
const searchingCities = ref(false);
let locationSearchTimeout: ReturnType<typeof setTimeout> | null = null;

// Logo
const logoInput = ref<HTMLInputElement | null>(null);
const defaultLogoUrl = computed(() => {
  return getLeagueImage('', 'h200');
});
const logoPreview = computed(() => {
  if (form.value.logoFile) {
    return URL.createObjectURL(form.value.logoFile);
  }
  if (form.value.logoUrl) {
    if (form.value.logoUrl.startsWith('http')) return form.value.logoUrl;
    return getLeagueImage(form.value.logoUrl, 'h200');
  }
  return '';
});

// Invite Modal
const showInviteModal = ref(false);
const playerCardRef = ref<InstanceType<typeof PlayerCardModal>>();
const inviteRole = ref<'admins' | 'timekeepers'>('admins');
const invitingForRole = ref<'admin' | 'timekeeper'>('admin');

// Display Team (repre tým) - pro akademie
const myTeams = ref<{ _id: string; name: string; imageUrl?: string }[]>([]);
const loadingMyTeams = ref(false);

const fetchMyTeams = async () => {
  loadingMyTeams.value = true;
  try {
    const response = await axiosInstance.get('/teams');
    myTeams.value = response.data.map((team: any) => ({
      _id: team._id,
      name: team.name,
      imageUrl: team?.imageUrl,
    }));
  } catch (e) {
    console.error('Error fetching teams:', e);
    myTeams.value = [];
  } finally {
    loadingMyTeams.value = false;
  }
};

const openDisplayTeamSelect = () => {
  if (myTeams.value.length === 0 && !loadingMyTeams.value) {
    fetchMyTeams();
  }
  currentView.value = 'displayTeamSelect';
};

const selectDisplayTeam = (team: { _id: string; name: string; imageUrl?: string } | null) => {
  form.value.displayTeam = team;
  currentView.value = 'main';
};

const inviteModalTitle = computed(() => {
  switch (invitingForRole.value) {
    case 'admin': return t('addAdmin');
    case 'timekeeper': return t('addTimekeeper');
    default: return t('invitePeople');
  }
});

const allSelectedUsers = computed(() => {
  // Only exclude users already in the role being invited, not across all roles
  if (inviteRole.value === 'admins') return [...form.value.admins];
  if (inviteRole.value === 'timekeepers') return [...form.value.timekeepers];
  return [...form.value.admins, ...form.value.timekeepers];
});

// Dirty tracking for edit mode
const originalFormSnapshot = ref('');

const isDirty = computed(() => {
  if (!originalFormSnapshot.value) return false;
  const { logoFile, ...rest } = form.value;
  return JSON.stringify(rest) !== originalFormSnapshot.value || logoFile !== null;
});

// Validation
const isFormValid = computed(() => {
  return form.value.name.trim().length >= 2 && 
         form.value.name.trim().length <= 24 &&
         form.value.location !== null &&
         form.value.admins.length > 0;
});

// Methods
const onLocationSearch = () => {
  if (locationSearchTimeout) clearTimeout(locationSearchTimeout);
  
  locationSearchTimeout = setTimeout(() => {
    if (!locationSearch.value || locationSearch.value.length < 2) {
      cityResults.value = [];
      return;
    }
    searchCities(locationSearch.value.trim());
  }, 300);
};

const searchCities = async (query: string) => {
  searchingCities.value = true;
  try {
    const url = new URL('https://nominatim.openstreetmap.org/search');
    url.searchParams.set('q', query);
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('addressdetails', '1');
    url.searchParams.set('limit', '10');
    url.searchParams.set('featuretype', 'city');

    const res = await fetch(url.toString(), {
      headers: { 'Accept': 'application/json' },
    });
    const data = await res.json();

    const mapped: LocationOption[] = (Array.isArray(data) ? data : []).map((r: any) => {
      const addr = r.address || {};
      const cityLike = addr.city || addr.town || addr.village || r.display_name?.split(',')?.[0] || r.name;
      const region = addr.state || addr.region || addr.county;
      const country = addr.country || 'Česko';
      return {
        id: Number(r.place_id),
        label: String(cityLike),
        subtitle: [region, country].filter(Boolean).join(', '),
        lat: r.lat,
        lon: r.lon,
        raw: r,
      };
    });

    const seen = new Set<string>();
    cityResults.value = mapped.filter(m => {
      const key = m.label.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch (e) {
    console.error('City search failed:', e);
    cityResults.value = [];
  } finally {
    searchingCities.value = false;
  }
};

const selectLocation = (city: LocationOption) => {
  form.value.location = city;
  currentView.value = 'main';
};

const selectLogo = () => {
  logoInput.value?.click();
};

const onLogoSelected = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const file = target.files?.[0];
  if (file) {
    form.value.logoFile = file;
  }
};

const removeLogo = () => {
  form.value.logoFile = null;
  form.value.logoUrl = '';
};

const openProfile = (user: UserLite) => {
  playerCardRef.value?.open(user);
};

const openInviteModal = (role: 'admin' | 'timekeeper') => {
  invitingForRole.value = role;
  inviteRole.value = role === 'admin' ? 'admins' : 'timekeepers';
  showInviteModal.value = true;
};

const handleInviteDone = (users: UserLite[]) => {
  for (const user of users) {
    if (invitingForRole.value === 'admin') {
      if (!form.value.admins.some(u => u._id === user._id)) {
        form.value.admins.push(user);
      }
    } else if (invitingForRole.value === 'timekeeper') {
      if (!form.value.timekeepers.some(u => u._id === user._id)) {
        form.value.timekeepers.push(user);
      }
    }
  }
  showInviteModal.value = false;
};

const removeAdmin = (index: number) => {
  form.value.admins.splice(index, 1);
};

const removeTimekeeper = (index: number) => {
  form.value.timekeepers.splice(index, 1);
};

// Bank account
const bankAccountValue = computed({
  get: () => ({
    prefix: form.value.bankAccountPrefix,
    number: form.value.bankAccountNumber,
    bankCode: form.value.bankCode,
  }),
  set: (val: { prefix: string; number: string; bankCode: string }) => {
    form.value.bankAccountPrefix = val.prefix;
    form.value.bankAccountNumber = val.number;
    form.value.bankCode = val.bankCode;
  },
});

const bankAccountDisplay = computed(() => {
  const number = form.value.bankAccountNumber;
  const code = form.value.bankCode;
  if (!number || !code) return t('notSet');
  const lastDigits = number.slice(-3);
  return `...${lastDigits}/${code}`;
});

// Seasons
const seasons = ref<ISeason[]>([]);
const pendingSeasons = ref<any[]>([]);
const seasonsLoading = ref(false);
const showSeasonForm = ref(false);
const seasonToEdit = ref<ISeason | null>(null);
const leagueObjectId = ref<string>('');
const showDeleteSeasonConfirm = ref(false);
const seasonToDeleteId = ref<string>('');
const deleteCountdown = ref(0);
let deleteCountdownTimer: ReturnType<typeof setInterval> | null = null;

const startDeleteCountdown = () => {
  deleteCountdown.value = 10;
  if (deleteCountdownTimer) clearInterval(deleteCountdownTimer);
  deleteCountdownTimer = setInterval(() => {
    deleteCountdown.value--;
    if (deleteCountdown.value <= 0) {
      if (deleteCountdownTimer) clearInterval(deleteCountdownTimer);
      deleteCountdownTimer = null;
    }
  }, 1000);
};

const clearDeleteCountdown = () => {
  if (deleteCountdownTimer) {
    clearInterval(deleteCountdownTimer);
    deleteCountdownTimer = null;
  }
  deleteCountdown.value = 0;
};

onBeforeUnmount(() => {
  clearDeleteCountdown();
});

const fetchSeasons = async () => {
  const id = leagueObjectId.value || props.leagueId;
  if (!id) return;
  seasonsLoading.value = true;
  try {
    const response = await axiosInstance.get(`/season/${id}`);
    seasons.value = Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error loading seasons:', error);
    seasons.value = [];
  } finally {
    seasonsLoading.value = false;
  }
};

const formatSeasonDates = (season: ISeason) => {
  if (season.dateFrom && season.dateTo) {
    return `${season.dateFrom} – ${season.dateTo}`;
  }
  return season.dateFrom || t('leagueForm.noDate');
};

const openSeasonForm = (season?: ISeason) => {
  seasonToEdit.value = season || null;
  showSeasonForm.value = true;
};

const editSeason = (season: any) => {
  openSeasonForm(season);
};

const deleteSeason = (seasonId: string) => {
  seasonToDeleteId.value = seasonId;
  showDeleteSeasonConfirm.value = true;
  startDeleteCountdown();
};

const cancelDeleteSeason = () => {
  showDeleteSeasonConfirm.value = false;
  clearDeleteCountdown();
};

const confirmDeleteSeason = async () => {
  if (deleteCountdown.value > 0) return;
  const seasonId = seasonToDeleteId.value;
  showDeleteSeasonConfirm.value = false;
  clearDeleteCountdown();

  // Check if it's a pending (local) season
  const isPending = pendingSeasons.value.some(s => s._localId === seasonId);
  if (isPending) {
    pendingSeasons.value = pendingSeasons.value.filter(s => s._localId !== seasonId);
    return;
  }

  try {
    await axiosInstance.delete(`/leagues/season/${seasonId}`);
    seasons.value = seasons.value.filter(s => s._id !== seasonId);
  } catch (error) {
    console.error('Error deleting season:', error);
  }
};

// Delete organization
const showDeleteOrgConfirm = ref(false);
const deleteOrgConfirmInput = ref('');

const isDeleteOrgConfirmValid = computed(() =>
  deleteOrgConfirmInput.value.toLowerCase() === t('delete').toLowerCase()
);

const cancelDeleteOrg = () => {
  showDeleteOrgConfirm.value = false;
  deleteOrgConfirmInput.value = '';
};

const executeDeleteOrg = async () => {
  if (!isDeleteOrgConfirmValid.value || !props.leagueId) return;
  showDeleteOrgConfirm.value = false;
  deleteOrgConfirmInput.value = '';
  try {
    await axiosInstance.delete(`/leagues/${props.leagueId}`);
    isOpen.value = false;
    emit('deleted', props.leagueId);
  } catch (error) {
    console.error('Error deleting organization:', error);
  }
};

// Merged list of saved + pending seasons
const allSeasons = computed(() => {
  return [...seasons.value, ...pendingSeasons.value];
});

const handleSeasonSaved = async (data: any) => {
  if (isEditing.value) {
    // Editing existing league — re-fetch from API
    await fetchSeasons();
  } else {
    // Creating new league — store locally
    const existingIdx = pendingSeasons.value.findIndex(s => s._localId === data._localId);
    if (existingIdx >= 0) {
      pendingSeasons.value[existingIdx] = data;
    } else {
      pendingSeasons.value.push(data);
    }
  }

  // After generate: close league modal too
  if (data?._generated) {
    isOpen.value = false;
  }
};

// Saving state
const isSaving = ref(false);

const handleSave = async () => {
  if (isSaving.value) return;
  isSaving.value = true;
  
  try {
    const locationData = form.value.location ? {
      city: form.value.location.label,
      region: form.value.location.raw?.address?.state || null,
      country: form.value.location.raw?.address?.country || 'Česko',
      lat: Number(form.value.location.lat),
      lon: Number(form.value.location.lon),
    } : null;

    const leagueData: any = {
      title: form.value.name.trim(),
      type: form.value.type,
      color: form.value.color,
      primaryColor: form.value.primaryColor,
      secondaryColor: form.value.secondaryColor,
      isPublic: form.value.isPublic,
      admins: form.value.admins.map(u => u._id),
      timekeepers: form.value.timekeepers.map(u => u._id),
      location: locationData,
      status: 'published',
      preferLeagueAccount: form.value.preferLeagueAccount,
      bankAccount: (form.value.bankAccountPrefix || form.value.bankAccountNumber || form.value.bankCode) ? {
        accountPrefix: form.value.bankAccountPrefix,
        accountNumber: form.value.bankAccountNumber,
        bankCode: form.value.bankCode,
      } : null,
      displayTeam: form.value.type === 'academy' ? (form.value.displayTeam?._id || null) : null,
    };

    let savedLeagueId: string;

    if (isEditing.value && props.leagueId) {
      // Update existing league
      await axiosInstance.put(`${API_BASE_URL}/leagues/${props.leagueId}`, leagueData);
      savedLeagueId = props.leagueId;
    } else {
      // Create new league
      const response = await axiosInstance.post(`${API_BASE_URL}/leagues`, leagueData);
      savedLeagueId = response.data.article._id;
    }

    // Upload logo if changed
    if (form.value.logoFile) {
      const formData = new FormData();
      formData.append('image', form.value.logoFile);
      await axiosInstance.post(`${API_BASE_URL}/leagues/${savedLeagueId}/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }

    // Create pending seasons for new league
    if (pendingSeasons.value.length > 0) {
      for (const pending of pendingSeasons.value) {
        try {
          const { _localId, year, ...seasonData } = pending;
          await axiosInstance.post('/leagues/season', {
            ...seasonData,
            _league: savedLeagueId,
          });
        } catch (err) {
          console.error('Error creating pending season:', err);
        }
      }
      pendingSeasons.value = [];
    }

    emit('saved', { ...form.value, _id: savedLeagueId });
    isOpen.value = false;
  } catch (error) {
    console.error('Error saving league:', error);
  } finally {
    isSaving.value = false;
  }
};

const handleClose = () => {
  currentView.value = 'main';
};

// Load league data for editing
const loadLeagueData = async () => {
  if (!props.leagueId) return;
  
  try {
    const { data: league } = await axiosInstance.get(`${API_BASE_URL}/leagues/${props.leagueId}`);
    
    // Parse location
    let parsedLocation: LocationOption | null = null;
    if (league.location) {
      if (typeof league.location === 'string') {
        parsedLocation = { id: 'loaded', label: league.location };
      } else if (typeof league.location === 'object') {
        parsedLocation = {
          id: 'loaded',
          label: league.location.label || league.location.city || '',
          subtitle: [league.location.region, league.location.country].filter(Boolean).join(', '),
          lat: String(league.location.lat ?? ''),
          lon: String(league.location.lon ?? ''),
          raw: {
            address: {
              state: league.location.region,
              country: league.location.country || 'Česko',
            }
          }
        };
      }
    }

    // Parse admins
    const rawAdmins = league.admins || [];
    let adminsList: UserLite[] = [];
    if (rawAdmins.length && typeof rawAdmins[0] === 'object') {
      adminsList = rawAdmins.map(mapUserLite);
    } else if (rawAdmins.length) {
      // IDs only - fetch details
      try {
        const res = await axiosInstance.get(`${API_BASE_URL}/users/by-ids`, { params: { ids: rawAdmins.join(',') } });
        const users = Array.isArray(res.data) ? res.data : res.data?.users || [];
        adminsList = users.map(mapUserLite);
      } catch {
        adminsList = rawAdmins.map((id: string) => ({ _id: id }));
      }
    }

    // Parse timekeepers
    const rawTimekeepers = league.timekeepers || [];
    let timekeepersList: UserLite[] = [];
    if (rawTimekeepers.length && typeof rawTimekeepers[0] === 'object') {
      timekeepersList = rawTimekeepers.map(mapUserLite);
    } else if (rawTimekeepers.length) {
      try {
        const res = await axiosInstance.get(`${API_BASE_URL}/users/by-ids`, { params: { ids: rawTimekeepers.join(',') } });
        const users = Array.isArray(res.data) ? res.data : res.data?.users || [];
        timekeepersList = users.map(mapUserLite);
      } catch {
        timekeepersList = rawTimekeepers.map((id: string) => ({ _id: id }));
      }
    }

    form.value = {
      name: league.title || '',
      location: parsedLocation,
      logoFile: null,
      logoUrl: league.imageUrl || '',
      isPublic: !!(league.isPublic ?? false),
      type: (league.type as 'league' | 'academy') || 'league',
      color: league.color || 'red',
      primaryColor: league.primaryColor || '#1a237e',
      secondaryColor: league.secondaryColor || '#ffc107',
      admins: adminsList,
      timekeepers: timekeepersList,
      bankAccountPrefix: league.bankAccount?.accountPrefix || '',
      bankAccountNumber: league.bankAccount?.accountNumber || '',
      bankCode: league.bankAccount?.bankCode || '',
      preferLeagueAccount: league.preferLeagueAccount !== false,
      displayTeam: league.displayTeam && typeof league.displayTeam === 'object'
        ? { _id: league.displayTeam._id, name: league.displayTeam.name, imageUrl: league.displayTeam.imageUrl }
        : null,
    };

    // Store actual MongoDB _id for season operations
    leagueObjectId.value = league._id;
  } catch (error) {
    console.error('Error loading league:', error);
  }
};

// Add current user as admin for new leagues
const addCurrentUserAsAdmin = () => {
  const user = userStore.user;
  if (user && form.value.admins.length === 0) {
    form.value.admins.push(mapUserLite(user));
  }
};

// Reset form when modal opens
watch(isOpen, async (val) => {
  if (val) {
    currentView.value = 'main';
    if (props.leagueId) {
      await loadLeagueData();
      const { logoFile, ...rest } = form.value;
      originalFormSnapshot.value = JSON.stringify(rest);
      fetchSeasons();
    } else {
      pendingSeasons.value = [];
      seasons.value = [];
      form.value = {
        name: '',
        location: null,
        logoFile: null,
        logoUrl: '',
        isPublic: true,
        type: 'league',
        color: 'red',
        primaryColor: '#1a237e',
        secondaryColor: '#ffc107',
        admins: [],
        timekeepers: [],
        bankAccountPrefix: '',
        bankAccountNumber: '',
        bankCode: '',
        preferLeagueAccount: true,
        displayTeam: null,
      };
      addCurrentUserAsAdmin();
    }
    // Clear search states
    locationSearch.value = '';
    cityResults.value = [];
  }
});
</script>

<style lang="scss" scoped>
// Views container
.views-container {
  position: relative;
  overflow: hidden;
  min-height: calc(92vh - 90px);
}

.main-view {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), 
              opacity 0.25s ease;
  
  &--hidden {
    opacity: 0.3;
    transform: translateX(-100px) scale(0.92);
    pointer-events: none;
  }
}

// Slide transition - iOS style
.slide-enter-active {
  transition: transform 0.35s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
}

.slide-enter-from {
  transform: translateX(100%) !important;
}

.slide-leave-to {
  transform: translateX(100%) !important;
}

.subview {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: 100%;
  max-width: 600px;
  height: 92vh;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-sheetBackground));
  border-radius: 32px 32px 0 0;
  z-index: 10000;
  overflow: hidden;
  transform: translateX(0);
}

.subview-header {
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
}

.subview-scroll {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 20px;
  padding-top: 70px;
}

.back-btn {
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: rgb(var(--v-theme-sheetCloseBtn));
  cursor: pointer;
  transition: all 0.2s ease;
  
  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
  
  &:hover {
    filter: brightness(0.92);
  }
  
  &:active {
    transform: scale(0.95);
  }
}

.subview-title {
  flex: 1;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
}

.spacer {
  width: 45px;
}

// Search
.search-input-wrapper-ios {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  
  .search-icon {
    color: rgba(var(--v-theme-sheetText), 0.5);
    margin-right: 12px;
  }
  
  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    font-size: 1rem;
    color: rgb(var(--v-theme-sheetText));
    
    &::placeholder {
      color: rgba(var(--v-theme-sheetText), 0.4);
    }
  }
}

.search-empty {
  padding: 24px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
}

.search-empty-inline {
  padding: 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.5);
  font-size: 0.9rem;
}

// Logo
.logo-section {
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.current-logo {
  width: 120px;
  height: 120px;
  border-radius: 32px;
  background: rgba(var(--v-theme-sheetText), 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.logo-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.logo-preview {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.hidden-input {
  display: none;
}

// Colors
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid rgba(var(--v-theme-sheetText), 0.25);
}

// Invite
.invite-actions {
  padding: 16px;
  margin-top: 16px;
}

// Phone input
.phone-input-row {
  display: flex;
  align-items: center;
  padding: 0 16px;
  min-height: 48px;
  gap: 12px;
}

.country-select {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.2s;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.15);
  }
}

.country-flag {
  font-size: 1.2rem;
}

.country-code {
  color: rgb(var(--v-theme-sheetText));
  font-size: 0.9rem;
}

.phone-number-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 1rem;
  color: rgb(var(--v-theme-sheetText));
  
  &::placeholder {
    color: rgba(var(--v-theme-sheetText), 0.4);
  }
}

.country-picker {
  margin: 8px 16px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  border-radius: 16px;
  overflow: hidden;
}

.country-option {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  transition: background 0.15s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.12);
  }
  
  .country-name {
    flex: 1;
    color: rgb(var(--v-theme-sheetText));
  }
  
  .country-code {
    color: rgba(var(--v-theme-sheetText), 0.6);
  }
}

// Text helpers
.text-disabled {
  color: rgba(var(--v-theme-sheetText), 0.4);
}

// Seasons
.season-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.season-delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:active {
    background: rgba(var(--v-theme-sheetText), 0.1);
  }
}

.empty-section {
  padding: 16px;
  text-align: center;
  color: rgba(var(--v-theme-sheetText), 0.4);
  font-size: 0.9rem;
}

// Delete confirm dialog
.delete-confirm-dialog {
  padding: 24px;
  text-align: center;
}

.delete-confirm-icon {
  margin-bottom: 16px;
}

.delete-confirm-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.delete-confirm-subtitle {
  font-size: 0.9rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-bottom: 16px;
}

.delete-confirm-input-wrap {
  margin-bottom: 20px;
}

.delete-confirm-input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid rgba(var(--v-theme-on-surface), 0.15);
  font-size: 0.95rem;
  text-align: center;
  background: transparent;
  color: inherit;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: rgb(var(--v-theme-error));
  }
}

.delete-confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.color-picker-section {
  padding: 16px;
}

.color-picker {
  background: transparent !important;
}

.delete-confirm-btn {
  padding: 10px 24px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &--cancel {
    background: rgba(var(--v-theme-on-surface), 0.1);
    color: inherit;
  }

  &--confirm {
    background: rgb(var(--v-theme-error));
    color: white;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }
}

.team-pick-logo {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
}

.delete-org-section {
  padding: 32px 16px 16px;
  text-align: center;
}

.delete-org-btn {
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  color: rgb(var(--v-theme-error));
  background: rgba(var(--v-theme-error), 0.1);
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.97);
  }
}
</style>
